<template>
    <!-- PRODUCT-26110 Task 5 (INSTANCE_ATTRS_CLASS_STYLE): see CriterionEnumInput.vue --
         inheritAttrs: false stops Vue 3 from auto-applying class/style to root. -->
    <div class="CriterionAndOrGroupInput" :class="$attrs.class" :style="$attrs.style">
        <FormElement :label="hideTitle ? null : form.schema.title">
            <template v-if="form.schema.description" #labelHint>
                <div
                    v-tooltip="form.schema.description"
                    class="CriterionAndOrGroupInput__hintIcon"
                >
                    <EidIcon :icon-src="faQuestionCircle" />
                </div>
            </template>

            <p class="CriterionAndOrGroupInput__subtitle">
                Select one or more options per set. All sets must be satisfied.
            </p>

            <div
                v-for="(set, index) in sets"
                :key="index"
                class="CriterionAndOrGroupInput__set"
                :class="{ 'CriterionAndOrGroupInput__set--highlighted': sets.length > 1 }"
            >
                <div v-if="sets.length > 1" class="CriterionAndOrGroupInput__setHeader">
                    <span class="CriterionAndOrGroupInput__setLabel">
                        {{ index === 0 ? 'Set 1 — any one of these' : `AND Set ${index + 1} — any one of these` }}
                    </span>
                    <NewButton
                        class="CriterionAndOrGroupInput__deleteButton"
                        variant="flat"
                        size="icon-sm"
                        color="neutral"
                        @click="removeSet(index)"
                    >
                        <EidIcon :icon-src="faXmark" alt="Remove set" />
                    </NewButton>
                </div>

                <NewDropdown
                    class="CriterionAndOrGroupInput__dropdown"
                    label="Select"
                    multi-select
                    enable-clear
                    :options="options"
                    :selected="setToOptions(set)"
                    @input="onSetInput(index, $event)"
                />

                <div
                    v-if="showValidationErrors && set.length === 0"
                    class="CriterionAndOrGroupInput__error"
                >
                    At least one selection is required
                </div>
            </div>

            <NewButton
                class="CriterionAndOrGroupInput__addButton"
                type="primary"
                :disabled="sets.length >= maxSets"
                @click="addSet"
            >
                <EidIcon :icon-src="faPlus" />
                {{ addSetLabel }}
            </NewButton>
        </FormElement>
    </div>
</template>

<style lang="scss">
.CriterionAndOrGroupInput {
    display: flex;
    flex-direction: column;
    gap: 12px;

    &__subtitle {
        font-size: 12px;
        color: var(--eidNeutralGrey600);
        margin: 0 0 4px;
    }

    &__set {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-bottom: 8px;

        &--highlighted {
            border-left: 3px solid var(--eidTrustGreen);
            padding-left: 10px;
        }
    }

    &__setHeader {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    &__setLabel {
        font-size: 13px;
        font-weight: 500;
    }

    &__error {
        font-size: 12px;
        color: var(--eidDangerRed);
    }

    &__dropdown {
        width: 100%;
    }

    &__hintIcon {
        display: inline-flex;
        align-items: center;
        margin-left: 4px;
        color: var(--eidNeutralGrey500);
        cursor: pointer;
    }
}
</style>

<script lang="ts">
    import { Component } from '@evidentid/vue-property-decorator';

    import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
    import { faPlus, faXmark } from '@fortawesome/pro-light-svg-icons';

    import NewDropdown from '@evidentid/dashboard-commons/components/Dropdown/NewDropdown.vue';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { FormElement } from '@evidentid/dashboard-commons/components/Form';
    import { JsonFormArray } from '@evidentid/json-schema/interfaces/JsonForm';

    import NewButton from '@/components/new-button/NewButton.vue';

    import AbstractCriterionInput from './AbstractCriterionInput';

    @Component({
        emits: [ 'input' ],
        components: {
            EidIcon,
            FormElement,
            NewDropdown,
            NewButton,
        },
        inheritAttrs: false,
    })
    export default class CriterionAndOrGroupInput extends AbstractCriterionInput<JsonFormArray, string[][]> {
        private faPlus = faPlus;
        private faXmark = faXmark;
        private faQuestionCircle = faQuestionCircle;

        private get sets(): string[][] {
            if (!Array.isArray(this.value) || this.value.length === 0) {
                return [ [] ];
            }
            return this.value;
        }

        private get availableOptions(): string[] {
            const schema = this.form.schema as any;
            return schema.items?.items?.enum ?? [];
        }

        private get options(): DropdownOption[] {
            return this.availableOptions.map((opt: string) => ({ value: opt, label: opt }));
        }

        private get maxSets(): number {
            return this.form.schema.maxItems ?? Infinity;
        }

        private get addSetLabel(): string {
            return this.form.schema.addItemTitle ?? 'Add another set';
        }

        private setToOptions(set: string[]): DropdownOption[] {
            return set.map((opt) => ({ value: opt, label: opt }));
        }

        private onSetInput(index: number, selected: DropdownOption[]): void {
            const updated = this.sets.map((set, i) => {
                if (i === index) {
                    return selected.map((opt) => opt.value as string);
                }
                return set;
            });
            this.$emit('input', updated);
        }

        private addSet(): void {
            this.$emit('input', [ ...this.sets, [] ]);
        }

        private removeSet(index: number): void {
            this.$emit('input', this.sets.filter((_, i) => i !== index));
        }
    }
</script>
